import React, { useState, useEffect } from 'react';
import TextField from '../../../components/Input/text-field';
import { ApiToken, FormMode, SpecificProjectPermission } from './types';
import ResourcePermissions from '../../role/edit-role/resource-permissions';

interface Props {
  saving: boolean;
  token?: ApiToken;
  editMode: FormMode;
  controllers?: any;
  projects?: any;
  showSpecificProjects: boolean;  
  onSave: (name: string, expiryDate: string, timezone: string, checkedResources: string[], selectedProjects: SpecificProjectPermission[]) => boolean | Promise<boolean>;
}

const ApiTokenForm: React.FC<Props> = ({ saving, token, onSave, editMode, controllers, projects, showSpecificProjects }) => {
  // Format date without timezone effects
  const toYYYYMMDD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const [name, setName] = useState('');
  const maxExpiryDate = new Date();
  maxExpiryDate.setFullYear(maxExpiryDate.getFullYear() + 1);
  const maxExpiryDateString = toYYYYMMDD(maxExpiryDate);
  const [expiryDate, setExpiryDate] = useState(maxExpiryDateString);
  const [checkedResources, setCheckedResources] = useState<Array<string>>([]);
  const [selectedProjects, setSelectedProjects] = useState<any[]>([]);
  const [sourceProjects, setSourceProjects] = useState<any[]>([]);

  useEffect(() => {
    if (token) {            
      if (editMode === FormMode.Renew) {
        setName(token.name + " " + new Date().toISOString().split('T')[0]);
        // Calculate the difference between created and expiry        
        const createdDate = new Date(token.created).toDateString();
        const expiryDateObj = new Date(token.expires);
        const diffMs = expiryDateObj.getTime() - new Date(createdDate).getTime();
        // Add the same difference to the current expiry
        const newExpiryDateObj = new Date(expiryDateObj.getTime() + diffMs);
        
        // Constrain to max one year from today
        const maxExpiryDateObj = new Date();
        maxExpiryDateObj.setFullYear(maxExpiryDateObj.getFullYear() + 1);
        const constrainedExpiryDate = newExpiryDateObj > maxExpiryDateObj ? maxExpiryDateObj : newExpiryDateObj;

        setExpiryDate(toYYYYMMDD(constrainedExpiryDate));
        setCheckedResources(token.resources?.map((r: any) => r.resourceId) ?? []);
        setSelectedProjects(token.projects ?? []);
      } else if (editMode === FormMode.Update) {
        setName(token.name);
        setExpiryDate(token.expires.split('T')[0]); // Use existing expiry date
        setCheckedResources(token.resources?.map((r: any) => r.resourceId) ?? []);        
        if (projects) {
           const projectsCopy = Array.from(projects);
          const existingProjects = Array.from(token?.projects);
          const existingProjectsWithName = existingProjects.map((existringProject: any) => {                 
                  const foundProject :any = projectsCopy.find((project: any) => project.id == existringProject.id);
                  return {...existringProject, name: foundProject?.name ?? existringProject.name };
              });
              
          setSelectedProjects(existingProjectsWithName ?? []);
        }
      } else {
        setExpiryDate(maxExpiryDateString);
      }
    }
  }, [token, editMode, maxExpiryDateString, projects]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    //if (!name.trim() || !expiryDate) return;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    try {
      const success = await onSave(name.trim(), expiryDate, timezone, checkedResources, selectedProjects);
      if (success) {
        setName('');
        setExpiryDate(maxExpiryDateString);
      }
    } catch (error) {
      // Handle error if needed
    }
  };

  const onChangeResourcesList = (e:any) => {            
        const value = e.target.value;
        if (checkedResources.includes(value)) {
            const newClaims = [...checkedResources].filter((id) => id !== value);
            setCheckedResources(newClaims);
        } else {
            const newClaims = [...checkedResources];
            newClaims.push(value);
            setCheckedResources(newClaims);
        }
    }

    const onAddResourcesList = (resourceIds:any[]) => {            
        const newClaims = Array.from(checkedResources);
        resourceIds.forEach((resourceId: any) => {
            if (!checkedResources.includes(resourceId)) {    
                newClaims.push(resourceId);
            }
        });
        
        setCheckedResources(newClaims);
    }

    const onRemoveResourcesList = (resourceIds:any[]) => {            
        const newClaims = Array.from(checkedResources);
        resourceIds.forEach((resourceId: any) => {
            var resoureceIndex = newClaims.indexOf(resourceId);
            if (resoureceIndex > -1) {    
                newClaims.splice(resoureceIndex, 1);
            }
        });
        
        setCheckedResources(newClaims);
    }

    const onAddProject = (project: any) => {
        const projectCopy = {...project, view: false, update: false };                
        const copy = [...selectedProjects, projectCopy];                
        orderProjects(copy);        
        setSelectedProjects(copy);

        const filteredProjects = sourceProjects.filter((p: any) => p.id !== project.id);
        setSourceProjects(filteredProjects);
    };

    const onRemoveProject = (project: any) => {
        const copy = [...sourceProjects, project];        
        orderProjects(copy);        
        setSourceProjects(copy);

        const filteredProjects = selectedProjects.filter((p: any) => p.id !== project.id);
        setSelectedProjects(filteredProjects);
    };

    const setPermission = (project: any, resource: any, permission: boolean) => {
        const selectedProjectsCopy = Array.from(selectedProjects);
        const selectedProjectIndex = selectedProjectsCopy.findIndex((p) => p.id === project.id);
        var selectedProject = selectedProjectsCopy.splice(selectedProjectIndex, 1)[0];
        selectedProject = {...selectedProject, [resource]: permission};
        
        const orderedProjects = [...selectedProjectsCopy, selectedProject];
        orderProjects(orderedProjects);
        setSelectedProjects(orderedProjects);
    };

    const orderProjects = (projects: any[]) => {        
        projects.sort((a: any, b: any) => {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          });
    };

  return (
    <form onSubmit={submit} style={{ marginBottom: 16 }}>
      {token?.created && <div style={{ marginBottom: 8 }}>Created: {new Date(token.created).toLocaleString()}</div>}
      {token?.partialToken && <div style={{ marginBottom: 8 }}>Partial Token: {token.partialToken}...</div>}
      <TextField
        type="text"
        label="Name"
        value={name}
        placeholder="Token Name"
        onChange={setName}
        readOnly={saving}
      />
      
      <TextField
        type="date" 
        label="Expiry Date" 
        readOnly={editMode === FormMode.Update || saving}
        value={expiryDate} 
        onChange={setExpiryDate}
        max={maxExpiryDateString} />     

        <ResourcePermissions 
            readOnly={editMode === FormMode.Renew || saving} 
            projects={projects} 
            selectedProjects={selectedProjects} 
            showSpecificProjects={showSpecificProjects} 
            controllers={controllers} 
            checkedResources={checkedResources} 
            onChangeResourcesList={onChangeResourcesList} 
            onAddResourcesList={onAddResourcesList} 
            onRemoveResourcesList={onRemoveResourcesList}
            onAddProject={onAddProject}
            onRemoveProject={onRemoveProject}
            setPermission={setPermission} />
      

      <button type="submit" className={`button is-primary mt-3 ${saving ? 'is-loading' : ''}`} disabled={saving} >{saving ? 'Saving...' : editMode === FormMode.Update ? 'Update Token' : editMode === FormMode.Add ? 'Create Token' : 'Renew Token'}</button>
    </form>
  );
};

export default ApiTokenForm;
